import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import {
  FaMoneyBillWave,
  FaCheckCircle,
  FaCalendarAlt,
  FaTimesCircle,
  FaSearch,
  FaSlidersH,
  FaChevronDown,
  FaChevronRight,
  FaExclamationTriangle,
  FaClock,
  FaPlay,
  FaStar,
  FaUserNurse,
  FaStethoscope,
  FaUserMd,
} from "react-icons/fa";
import secureLocalStorage from "react-secure-storage";

import { CommonImages } from "../../../../reusableUI/images";
import { getPartnerDashboard } from "../../../../Service/partnerAPI";
import { showToast } from "../../../../reusableUI/toaster";
import moment from "moment";

/* =========================================================
HELPERS
========================================================= */
const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const cx = (...classes) => classes.filter(Boolean).join(" ");

const FILTERS = [
  { key: "all", label: "All" },
  { key: "today", label: "Today" },
  { key: "2days", label: "Last 2 Days" },
  { key: "7days", label: "Last 7 Days" },
  { key: "custom", label: "Custom" },
];

const DONUT_BLUE = "#2563eb";
const DONUT_GREEN = "#22c55e";

const pad2 = (x) => String(x).padStart(2, "0");
const toYMD = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const formatHuman = (ymd) => {
  if (!ymd) return "";
  const d = new Date(ymd);
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
};

const getRangeForFilter = (filterKey) => {
  const today = new Date();
  const end = new Date(today);

  if (filterKey === "today") {
    const ymd = toYMD(today);
    return { from: ymd, to: ymd };
  }

  if (filterKey === "2days") {
    const start = new Date(today);
    start.setDate(start.getDate() - 1);
    return { from: toYMD(start), to: toYMD(end) };
  }

  if (filterKey === "7days") {
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    return { from: toYMD(start), to: toYMD(end) };
  }

  if (filterKey === "all") {
    return { from: "2000-01-01", to: toYMD(end) };
  }

  if (filterKey === "custom") {
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    return { from: toYMD(start), to: toYMD(end) };
  }

  // custom -> do not auto change from/to
  return null;
};

const dedupeBy = (arr, keyFn) => {
  const seen = new Set();
  return (Array.isArray(arr) ? arr : []).filter((item) => {
    const key = keyFn(item);
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const pctOf = (a, b) => {
  const denom = Number(b || 0);
  if (!denom) return "0.00";
  return ((Number(a || 0) / denom) * 100).toFixed(2);
};

/* =========================================================
MAIN COMPONENT
========================================================= */
const PartnerDashboard = ({ partnerID }) => {
  const [filter, setFilter] = useState("today");
  const navigate = useNavigate();
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [partnerName, setPartnerName] = useState("");

  const [showPatientModal, setShowPatientModal] = useState(false);
  const [patientSearch, setPatientSearch] = useState("");
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSearch, setFeedbackSearch] = useState("");

  const [revenueTab, setRevenueTab] = useState("service");
  const [performerTab, setPerformerTab] = useState("nursing");

  const initialRange = getRangeForFilter("today");
  const [from, setFrom] = useState(() => initialRange?.from);
  const [to, setTo] = useState(() => initialRange?.to);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFromChange = (val) => {
    if (!val) {
      setFrom("");
      return;
    }
    const todayStr = toYMD(new Date());
    if (val > todayStr) {
      showToast("From date cannot be in the future", "Warning");
      return;
    }
    if (to && val > to) {
      showToast("From date cannot be after To date", "Warning");
      return;
    }
    setFrom(val);
  };

  const handleToChange = (val) => {
    if (!val) {
      setTo("");
      return;
    }
    const todayStr = toYMD(new Date());
    if (val > todayStr) {
      showToast("To date cannot be in the future", "Warning");
      return;
    }
    if (from && val < from) {
      showToast("To date cannot be before From date", "Warning");
      return;
    }
    setTo(val);
  };

  // update date range when filter changes (except custom)
  useEffect(() => {
    const storedPartnerName = secureLocalStorage.getItem("partnerName");
    const r = getRangeForFilter(filter);
    if (r?.from && r?.to) {
      setFrom(r.from);
      setTo(r.to);
    }
    setPartnerName(storedPartnerName || "");
  }, [filter]);

  // fetch API when from/to changes
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);

        const res = await getPartnerDashboard(from, to, partnerID);

        const ok =
          res?.success === true ||
          res?.error === false ||
          res?.data?.success === true ||
          res?.data?.error === false;

        const dashboard = res?.data?.activePatients
          ? res.data
          : res?.data?.data?.activePatients
            ? res.data.data
            : res?.data?.activePatients
              ? res.data
              : res?.activePatients
                ? res
                : res?.data || null;

        if (ok && dashboard) setData(dashboard);
        else setData(null);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    if (from && to) fetchDashboard();
  }, [from, to, partnerID]);

  const headerHint = useMemo(
    () => FILTERS.find((f) => f.key === filter)?.label || "Overview",
    [filter],
  );

  console.log(data);

  const dateHint = useMemo(() => {
    const apiStart = data?.filter?.startDate || from;
    const apiEnd = data?.filter?.endDate || to;
    if (!apiStart || !apiEnd) return "";
    if (apiStart === apiEnd) return formatHuman(apiStart);
    return `${formatHuman(apiStart)} - ${formatHuman(apiEnd)}`;
  }, [data?.filter?.startDate, data?.filter?.endDate, from, to]);

  // Active patients
  const activePatientsCount = data?.activePatients?.totalPatients ?? 0;
  const roleCounts = data?.activePatients?.roleCounts ?? {};
  const nurseCount = roleCounts?.nurse ?? 0;
  const physioCount = roleCounts?.physiotherapist ?? 0;
  const doctorCount = roleCounts?.doctor ?? 0;

  // Progress
  const progress = data?.progressOverview ?? {};
  const scheduled = progress?.scheduled ?? 0;
  const started = progress?.started ?? 0;
  const completed = progress?.completed ?? 0;
  const billingDone = progress?.billingDone ?? 0;
  const notPaidOrExpired = progress?.notPaidOrExpired ?? 0;
  const cancelled = progress?.cancelled ?? 0;

  // Collection summary
  const collectionToday = data?.collectionSummary?.processedCollection ?? 0;

  // Share donut
  const companyAmount = data?.serviceProviderShare?.companyAmount ?? 0;
  const providerAmount = data?.serviceProviderShare?.providerAmount ?? 0;
  const totalCollection = companyAmount + providerAmount;

  const donutData = useMemo(
    () => [
      {
        name: "Hospital Share",
        value: companyAmount,
        pct: data?.serviceProviderShare?.companyPercentage ?? 0,
        color: DONUT_BLUE,
      },
      {
        name: "Provider Share",
        value: providerAmount,
        pct: data?.serviceProviderShare?.providerPercentage ?? 0,
        color: DONUT_GREEN,
      },
    ],
    [
      companyAmount,
      providerAmount,
      data?.serviceProviderShare?.companyPercentage,
      data?.serviceProviderShare?.providerPercentage,
    ],
  );

  const totals = useMemo(
    () => ({
      hospitalShare: companyAmount,
      providerShare: providerAmount,
      commissionPaid: companyAmount, // API doesn't have separate commission, showing companyAmount
      netCollection: totalCollection,
    }),
    [companyAmount, providerAmount, totalCollection],
  );

  // Top revenue generators
  const revenueRows = useMemo(() => {
    const rows = (data?.topRevenueGenerators || []).map((x) => ({
      category: x?.label || x?.role || "—",
      value: Number(x?.collection || 0),
      _key: `${x?.label || x?.role}-${x?.collection}`,
    }));
    return dedupeBy(rows, (r) => r._key);
  }, [data?.topRevenueGenerators]);

  const revenueMax = useMemo(
    () => Math.max(1, ...revenueRows.map((x) => x.value)),
    [revenueRows],
  );

  // Top performers
  const performerRows = useMemo(() => {
    const matchRole = (role = "") => {
      const r = String(role).toLowerCase();
      if (performerTab === "nursing") return r.includes("nurse");
      if (performerTab === "physio") return r.includes("physio");
      if (performerTab === "doctors") return r.includes("doctor");
      if (performerTab === "lab") return r.includes("lab");
      return true;
    };

    const rows = (data?.topPerformers || [])
      .filter((x) => matchRole(x?.role))
      .map((x) => ({
        name: x?.name || "—",
        completed: Number(x?.completedDuties || 0),
        rating: Number(x?.averageRating || 0),
        collection: Number(x?.collection || 0),
        _key: x?.userID || x?.name,
      }))
      .sort((a, b) => b.completed - a.completed);

    return dedupeBy(rows, (r) => r._key).slice(0, 5);
  }, [data?.topPerformers, performerTab]);

  // Feedback
  const feedbackStats = useMemo(
    () => ({
      avg: Number(data?.patientFeedback?.averageRating ?? 0),
      totalFeedback: Number(data?.patientFeedback?.totalFeedback ?? 0),
      negativeFeedback: Number(data?.patientFeedback?.negativeFeedback ?? 0),
    }),
    [data?.patientFeedback],
  );

  const feedbackRows = useMemo(() => {
    const rows = Array.isArray(data?.patientFeedback?.recentFeedback)
      ? data.patientFeedback.recentFeedback
      : [];
    return rows.map((r, idx) => ({
      name: r?.userName || r?.patientName || r?.name || "—",
      hcpName: r?.hcpName || "—",
      rating: Number(r?.rating ?? 0),
      message: r?.review || r?.message || r?.comment || "—",
      time: r?.date
        ? moment(r.date).format("DD/MM/YYYY")
        : r?.time || r?.createdAt || "",
      _key: r?._id || `${idx}`,
    }));
  }, [data?.patientFeedback?.recentFeedback]);

  const feedbackFiltered = useMemo(() => {
    const q = feedbackSearch.trim().toLowerCase();
    if (!q) return feedbackRows;
    return feedbackRows.filter((r) =>
      `${r.name} ${r.hcpName} ${r.message}`.toLowerCase().includes(q),
    );
  }, [feedbackRows, feedbackSearch]);

  // Parked
  const criticalAlertsRows = useMemo(() => {
    const raw = data?.parked?.criticalCare;
    const rows = Array.isArray(raw) ? raw : [];
    return rows.map((a, idx) => ({
      patient: a?.patientName || a?.patient || "—",
      issue: a?.issue || a?.title || "—",
      raisedBy: a?.raisedBy || a?.staffName || "—",
      actionTaken: a?.actionTaken || "—",
      status: a?.status || "Pending",
      _key: a?._id || `${idx}`,
    }));
  }, [data?.parked?.criticalCare]);

  const incompleteServicesRows = useMemo(() => {
    const raw = data?.parked?.incompleteService;
    const rows = Array.isArray(raw) ? raw : [];
    return rows.map((x, idx) => ({
      patient: x?.patientName || x?.patient || "—",
      serviceMissed: x?.service || x?.serviceName || "—",
      reason: x?.reason || "—",
      correctiveAction: x?.correctiveAction || "—",
      rescheduled: x?.rescheduled || "—",
      _key: x?._id || `${idx}`,
    }));
  }, [data?.parked?.incompleteService]);

  // Patient list modal (safe)
  const activePatientsList = useMemo(() => {
    const raw = data?.activePatients?.visits;
    if (!Array.isArray(raw)) return [];
    return dedupeBy(
      raw.map((v, idx) => ({
        patient: v?.patientName || v?.patient || `Patient ${idx + 1}`,
        diagnosis: v?.serviceRequested || "—",
        service: v?.specialistRole || v?.serviceName || v?.label || "—",
        staff: v?.specialistName || v?.staff || v?.providerName || "—",
        time: v?.time || v?.slotTime || v?.visitTime || "—",
        date:
          v?.startDate && v?.endDate
            ? moment(v?.startDate).format("DD/MM/YYYY") +
              " - " +
              moment(v?.endDate).format("DD/MM/YYYY")
            : "—",
        status: v?.status || "Ongoing",
        _key: v?.visitID || v?._id || `${idx}`,
      })),
      (x) => x._key,
    );
  }, [data?.activePatients?.matchedVisits]);

  const canOpenPatientList = activePatientsList.length > 0;

  const activePatientsFiltered = useMemo(() => {
    const q = patientSearch.trim().toLowerCase();
    if (!q) return activePatientsList;
    return activePatientsList.filter((p) =>
      `${p.patient} ${p.staff} ${p.diagnosis} ${p.service} ${p.status}`
        .toLowerCase()
        .includes(q),
    );
  }, [activePatientsList, patientSearch]);

  return (
    <div className="h-[88vh] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-[0_20px_60px_rgba(15,23,42,0.10)] m-2 flex flex-col">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-30 border-b border-slate-200 bg-white px-6 py-4 flex flex-col min-[1100px]:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h2 className="text-lg md:text-lg font-bold text-blue-600 tracking-tight">
            Dashboard
          </h2>
          <p className="text-xs text-slate-500 font-semibold mt-0.5">
            {headerHint} overview {loading ? "(Loading...)" : ""}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-start lg:justify-end">
          {/* Filter */}
          <div className="relative">
            <button
              onClick={() => setShowFilterDropdown((s) => !s)}
              className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2 text-xs font-extrabold text-slate-800 shadow-sm hover:bg-slate-50 transition"
            >
              <FaSlidersH className="text-slate-400 text-sm" />
              {FILTERS.find((f) => f.key === filter)?.label}
              <FaChevronDown className="text-slate-400 text-[10px]" />
            </button>

            {showFilterDropdown && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowFilterDropdown(false)}
                />
                <div className="absolute right-0 mt-2 w-56 z-50 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                  <div className="px-3 py-2 text-[10px] font-extrabold text-slate-500 uppercase border-b border-slate-100">
                    Select duration
                  </div>
                  {FILTERS.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => {
                        setFilter(f.key);
                        setShowFilterDropdown(false);
                      }}
                      className={cx(
                        "w-full text-left px-4 py-2 text-xs font-extrabold hover:bg-slate-50 transition flex items-center justify-between",
                        filter === f.key
                          ? "text-blue-700 bg-blue-50/50"
                          : "text-slate-800",
                      )}
                    >
                      {f.label}
                      {filter === f.key && (
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Custom date range */}
          {filter === "custom" && (
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2 shadow-sm">
              <div className="text-[11px] font-bold text-slate-500">From</div>
              <input
                type="date"
                value={from || ""}
                max={toYMD(new Date())}
                onChange={(e) => handleFromChange(e.target.value)}
                className="text-xs font-extrabold text-slate-800 outline-none bg-transparent"
              />
              <div className="text-[11px] font-bold text-slate-500">To</div>
              <input
                type="date"
                value={to || ""}
                min={from || ""}
                max={toYMD(new Date())}
                onChange={(e) => handleToChange(e.target.value)}
                className="text-xs font-extrabold text-slate-800 outline-none bg-transparent"
              />
            </div>
          )}

          {/* Profile */}
          <div className="flex items-center gap-2 pl-1">
            {/* <div className="h-9 w-9 rounded-full overflow-hidden border border-slate-200 shadow-sm bg-slate-100">
                            <img
                                src={CommonImages.agency}
                                alt="Admin"
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                    e.currentTarget.src =
                                        "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&q=80&w=150";
                                }}
                            />
                        </div> */}
            <div className="hidden md:block leading-tight">
              {/* <div className="text-xs font-extrabold text-slate-900">
                                {partnerName}
                            </div> */}
            </div>
            {/* <FaChevronDown className="text-slate-400 text-[10px] hidden md:block" /> */}
          </div>
        </div>
      </div>

      {/* ================= CONTENT ================= */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {/* Row 1 */}
        <div className="grid grid-cols-1 min-[1100px]:grid-cols-12 gap-4">
          <ActivePatientsSummaryCard
            className="min-[1100px]:col-span-4"
            patients={activePatientsCount}
            nurseCount={nurseCount}
            physioCount={physioCount}
            doctorCount={doctorCount}
            onViewList={() => setShowPatientModal(true)}
            canOpenPatientList={canOpenPatientList}
          />

          <ProgressOverviewCard
            className="min-[1100px]:col-span-4"
            scheduled={scheduled}
            started={started}
            completed={completed}
            billingDone={billingDone}
            delayed={notPaidOrExpired}
            cancelled={cancelled}
          />

          <CollectionSummaryCard
            className="min-[1100px]:col-span-4"
            today={collectionToday}
          />
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 min-[1100px]:grid-cols-12 gap-4">
          <IncompleteServicesCard
            className="min-[1100px]:col-span-6 h-[320px]"
            rows={incompleteServicesRows}
          />
          <CriticalAlertsCard
            className="min-[1100px]:col-span-6"
            rows={criticalAlertsRows}
          />
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-1 min-[1100px]:grid-cols-12 gap-4">
          <TopRevenueGeneratorsCard
            className="min-[1100px]:col-span-6"
            tab={revenueTab}
            setTab={setRevenueTab}
            rows={revenueRows}
            maxValue={revenueMax}
          />
          <TopPerformersCard
            className="min-[1100px]:col-span-6"
            tab={performerTab}
            setTab={setPerformerTab}
            rows={performerRows}
          />
        </div>

        {/* Row 4 */}
        <div className="grid grid-cols-1 min-[1100px]:grid-cols-12 gap-4">
          <ServiceProviderShareCard
            className="min-[1100px]:col-span-6"
            donutData={donutData}
            totalCollection={totalCollection}
            hospitalValue={totals.hospitalShare}
            providerValue={totals.providerShare}
            commissionPaid={totals.commissionPaid}
            netCollection={totals.netCollection}
          />

          <PatientFeedbackTodayCard
            className="min-[1100px]:col-span-6 h-[320px]"
            avg={feedbackStats.avg}
            totalFeedback={feedbackStats.totalFeedback}
            negativeFeedback={feedbackStats.negativeFeedback}
            rows={feedbackRows}
            onViewAll={() => setShowFeedbackModal(true)}
          />
        </div>
      </div>

      {/* ================= MODAL ================= */}
      {showPatientModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl h-[85vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <div className="text-lg font-extrabold text-slate-900">
                  Active Patient Services
                </div>
                <div className="text-[11px] text-slate-500 font-semibold mt-0.5">
                  Date range: {dateHint || `${from} - ${to}`}
                </div>
                {!canOpenPatientList && (
                  <div className="text-[11px] text-amber-700 font-semibold mt-1">
                    Patient list not available from API.
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowPatientModal(false)}
                className="h-10 w-10 rounded-full bg-white border border-slate-200 text-slate-500 hover:text-slate-900 flex items-center justify-center shadow-sm hover:bg-slate-50 transition"
              >
                <FaTimesCircle className="text-xl" />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              <div className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 bg-white">
                <FaSearch className="text-slate-400 text-[12px]" />
                <input
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  placeholder="Search patient, staff, diagnosis..."
                  className="outline-none w-full bg-transparent text-sm font-semibold text-slate-800 placeholder-slate-400"
                  disabled={!canOpenPatientList}
                />
              </div>

              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-[10px] font-extrabold text-slate-500">
                        <th className="px-4 py-3 text-left">Patient</th>
                        <th className="px-4 py-3 text-left">
                          Diagnosis/Service
                        </th>
                        <th className="px-4 py-3 text-left">Specialist</th>
                        <th className="px-4 py-3 text-left">Staff</th>
                        <th className="px-4 py-3 text-left">Date</th>
                        <th className="px-4 py-3 text-left">Time</th>
                        <th className="px-4 py-3 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {canOpenPatientList && activePatientsFiltered.length ? (
                        activePatientsFiltered.map((p, idx) => (
                          <tr
                            key={`${p._key}-${idx}`}
                            className="hover:bg-slate-50/70 transition"
                          >
                            <td className="px-4 py-3 font-extrabold text-slate-900 whitespace-nowrap">
                              {p.patient}
                            </td>
                            <td className="px-4 py-3 text-slate-700 font-semibold">
                              {p.diagnosis}
                            </td>
                            <td className="px-4 py-3 text-slate-700 font-semibold">
                              {p.service}
                            </td>
                            <td className="px-4 py-3 text-slate-900 font-bold whitespace-nowrap">
                              {p.staff}
                            </td>
                            <td className="px-4 py-3 text-slate-600 font-semibold whitespace-nowrap">
                              {p.date}
                            </td>
                            <td className="px-4 py-3 text-slate-600 font-semibold whitespace-nowrap">
                              {p.time}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <StatusBadge status={p.status} />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-10 text-center text-slate-500 font-semibold"
                          >
                            {canOpenPatientList
                              ? "No patients match your search."
                              : "No list available from API."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="text-[11px] text-slate-500 font-semibold">
                Showing{" "}
                <span className="font-extrabold text-slate-900">
                  {activePatientsFiltered.length}
                </span>{" "}
                of{" "}
                <span className="font-extrabold text-slate-900">
                  {activePatientsList.length}
                </span>{" "}
                records
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end">
              <button
                onClick={() => setShowPatientModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md font-extrabold hover:bg-blue-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showFeedbackModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl h-[85vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <div className="text-lg font-extrabold text-slate-900">
                  All Patient Feedbacks
                </div>
                <div className="text-[11px] text-slate-500 font-semibold mt-0.5">
                  Date range: {dateHint || `${from} - ${to}`}
                </div>
              </div>
              <button
                onClick={() => setShowFeedbackModal(false)}
                className="h-10 w-10 rounded-full bg-white border border-slate-200 text-slate-500 hover:text-slate-900 flex items-center justify-center shadow-sm hover:bg-slate-50 transition"
              >
                <FaTimesCircle className="text-xl" />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              <div className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 bg-white">
                <FaSearch className="text-slate-400 text-[12px]" />
                <input
                  value={feedbackSearch}
                  onChange={(e) => setFeedbackSearch(e.target.value)}
                  placeholder="Search patient, staff, message..."
                  className="outline-none w-full bg-transparent text-sm font-semibold text-slate-800 placeholder-slate-400"
                />
              </div>

              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-[10px] font-extrabold text-slate-500">
                        <th className="px-4 py-3 text-left">Patient</th>
                        <th className="px-4 py-3 text-left">Staff (HCP)</th>
                        <th className="px-4 py-3 text-left">Rating</th>
                        <th className="px-4 py-3 text-left">Review Message</th>
                        <th className="px-4 py-3 text-right">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {feedbackFiltered.length ? (
                        feedbackFiltered.map((r, idx) => (
                          <tr
                            key={`${r._key}-${idx}`}
                            className="hover:bg-slate-50/70 transition"
                          >
                            <td className="px-4 py-3 font-extrabold text-slate-900 whitespace-nowrap">
                              {r.name}
                            </td>
                            <td className="px-4 py-3 text-slate-700 font-semibold whitespace-nowrap">
                              {r.hcpName}
                            </td>
                            <td className="px-4 py-3 text-slate-700 font-semibold whitespace-nowrap">
                              <Stars value={r.rating} />
                            </td>
                            <td className="px-4 py-3 text-slate-900 font-medium max-w-xs break-words">
                              “{r.message}”
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600 font-semibold whitespace-nowrap">
                              {r.time}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-10 text-center text-slate-500 font-semibold"
                          >
                            No feedbacks match your search.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="text-[11px] text-slate-500 font-semibold">
                Showing{" "}
                <span className="font-extrabold text-slate-900">
                  {feedbackFiltered.length}
                </span>{" "}
                of{" "}
                <span className="font-extrabold text-slate-900">
                  {feedbackRows.length}
                </span>{" "}
                records
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end">
              <button
                onClick={() => setShowFeedbackModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md font-extrabold hover:bg-blue-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* =========================================================
REUSABLE UI
========================================================= */
const Card = ({ className, children }) => (
  <div
    className={cx(
      "bg-white rounded-xl border border-slate-200 shadow-sm",
      className,
    )}
  >
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    Resolved: "bg-emerald-50 text-emerald-800 border-emerald-100",
    Pending: "bg-amber-50 text-amber-800 border-amber-100",
    Delayed: "bg-amber-50 text-amber-800 border-amber-100",
    Cancelled: "bg-rose-50 text-rose-800 border-rose-100",
    Ongoing: "bg-blue-50 text-blue-800 border-blue-100",
    Completed: "bg-emerald-50 text-emerald-800 border-emerald-100",
    Upcoming: "bg-amber-50 text-amber-800 border-amber-100",
  };
  return (
    <span
      className={cx(
        "px-2.5 py-1 rounded-full border text-[10px] font-extrabold inline-flex items-center justify-center",
        map[status] || "bg-slate-50 text-slate-700 border-slate-200",
      )}
    >
      {status}
    </span>
  );
};

const Stars = ({ value = 0 }) => {
  const v = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <FaStar
          key={i}
          className={cx(
            "text-[11px]",
            i < v ? "text-amber-500" : "text-slate-300",
          )}
        />
      ))}
    </span>
  );
};

/* =========================================================
CARD: 1. Active Patients
========================================================= */
const ActivePatientsSummaryCard = ({
  className,
  patients,
  nurseCount,
  physioCount,
  doctorCount,
  onViewList,
  canOpenPatientList,
}) => (
  <Card className={className}>
    <div className="p-4">
      <div className="text-sm font-extrabold text-slate-900">
        1. Active Patients
      </div>

      <div className="mt-2 flex items-end gap-2">
        <div className="text-3xl font-black text-slate-900">{patients}</div>
        <div className="text-[11px] font-bold text-slate-500 mb-1">
          Patients
        </div>
      </div>

      <div className="mt-2 flex flex-col gap-3">
        <MiniRoleStat
          tone="blue"
          icon={<FaUserNurse />}
          label="Nurse"
          value={nurseCount}
        />
        <MiniRoleStat
          tone="emerald"
          icon={<FaStethoscope />}
          label="Physiotherapist"
          value={physioCount}
        />
        <MiniRoleStat
          tone="amber"
          icon={<FaUserMd />}
          label="Doctor"
          value={doctorCount}
        />
      </div>

      {canOpenPatientList && (
        <button
          onClick={onViewList}
          className="mt-6 w-full py-2.5 rounded-md border border-blue-500 transition text-[12px] font-extrabold flex items-center justify-center gap-2 bg-white hover:bg-blue-50 text-blue-600"
        >
          View Patient List <FaChevronRight className="text-[10px]" />
        </button>
      )}
    </div>
  </Card>
);

const MiniRoleStat = ({ icon, label, value, tone }) => {
  const toneMap = {
    blue: "text-blue-600",
    emerald: "text-emerald-600",
    amber: "text-amber-500",
  };

  return (
    <div className="flex items-center gap-3 py-1 pl-2">
      <div className={cx("text-lg", toneMap[tone])}>{icon}</div>

      <div className="text-base font-bold text-slate-900 min-w-[32px]">
        {Number(value || 0).toLocaleString("en-IN")}
      </div>

      <div className="text-sm text-slate-500 font-medium">{label}</div>
    </div>
  );
};

/* =========================================================
CARD: 2. Progress Overview
========================================================= */
const ProgressOverviewCard = ({
  className,
  scheduled,
  started,
  completed,
  billingDone,
  delayed,
  cancelled,
}) => {
  const denom = Math.max(1, scheduled, started, completed, billingDone);
  const pct = (n) => `${Math.round((Number(n || 0) / denom) * 100)}%`;

  return (
    <Card className={className}>
      <div className="p-4">
        <div className="text-sm font-extrabold text-slate-900">
          2. Progress Overview
        </div>

        <div className="ml-2 mt-8 grid grid-cols-1 sm:grid-cols-12 items-center gap-3">
          <Step
            title="Scheduled"
            value={scheduled}
            pct={pct(scheduled)}
            tone="blue"
            icon={<FaCalendarAlt />}
          />
          <Arrow />
          <Step
            title="Started"
            value={started}
            pct={pct(started)}
            tone="emerald"
            icon={<FaPlay className="text-[12px]" />}
          />
          <Arrow />
          <Step
            title="Completed"
            value={completed}
            pct={pct(completed)}
            tone="emerald"
            icon={<FaCheckCircle />}
          />
          <Arrow />
          <Step
            title="Billing Done"
            value={billingDone}
            pct={pct(billingDone)}
            tone="teal"
            icon={<FaMoneyBillWave className="text-[14px]" />}
          />
        </div>

        <div className="mt-12 rounded-md border border-orange-400  p-2.5 flex items-center justify-center gap-8">
          <div className="flex items-center gap-2 text-xs font-extrabold text-slate-700">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            Not paid/Expired : <span className="text-slate-900">{delayed}</span>
          </div>
          <div className="flex items-center gap-2 text-xs font-extrabold text-slate-700">
            <span className="h-2 w-2 rounded-full bg-rose-500" />
            Cancelled: <span className="text-slate-900">{cancelled}</span>
          </div>
        </div>
      </div>
    </Card>
  );
};

const Arrow = () => (
  <div className="hidden sm:flex justify-center text-slate-300 text-lg font-black leading-none -mt-2">
    →
  </div>
);

const Step = ({ title, value, pct, tone, icon }) => {
  const toneMap = {
    blue: "bg-blue-50 border-blue-100 text-blue-700",
    amber: "bg-amber-50 border-amber-100 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    teal: "bg-teal-50 border-teal-100 text-teal-700",
  };

  return (
    <div className="sm:col-span-2 flex flex-col items-center text-center">
      <div
        className={cx(
          "h-10 w-10 rounded-full border flex items-center justify-center",
          toneMap[tone],
        )}
      >
        {icon}
      </div>
      <div className="mt-2 text-[11px] font-extrabold text-slate-600">
        {title}
      </div>
      <div className="text-sm font-black text-slate-900 mt-0.5">
        {Number(value || 0).toLocaleString("en-IN")}
      </div>
      <div className="text-[10px] font-bold text-slate-500">{pct}</div>
    </div>
  );
};

/* =========================================================
CARD: 3. Collection Summary (ONLY TODAY)
========================================================= */
const CollectionSummaryCard = ({ className, today }) => {
  const navigate = useNavigate();
  return (
    <Card className={className}>
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 flex items-center justify-center font-black">
              ₹
            </div>
            <div className="text-sm font-extrabold text-slate-900">
              3. Collection Summary
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 flex flex-col">
        {/* Centered Amount Section */}
        <div className="flex justify-center">
          <MetricBlock label="Total Amount" value={inr(today)} big center />
        </div>

        <button
          onClick={() => navigate("/partner/payouts")}
          className="mt-8 w-full py-2.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 font-extrabold text-xs hover:bg-emerald-100 transition flex items-center justify-center gap-2"
        >
          View Collection Report <FaChevronRight className="text-[10px]" />
        </button>
      </div>
    </Card>
  );
};

const MetricBlock = ({ label, value, big, center }) => (
  <div
    className={cx(
      "rounded-md border border-slate-200 bg-white p-4 w-full",
      center && "text-center flex flex-col items-center justify-center",
      big ? "min-h-[120px] max-w-[320px]" : "max-w-[320px]",
    )}
  >
    <div className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">
      {label}
    </div>

    <div
      className={cx(
        "mt-2 font-black text-slate-900",
        big ? "text-2xl" : "text-sm",
      )}
    >
      {value}
    </div>
  </div>
);

/* =========================================================
CARD: Critical Alerts
========================================================= */
const CriticalAlertsCard = ({ className, rows }) => (
  <Card className={className}>
    <div className="p-4 border-b border-slate-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-rose-50 border border-rose-100 text-rose-700 flex items-center justify-center">
            <FaExclamationTriangle className="text-[13px]" />
          </div>
          <div className="text-sm font-extrabold text-slate-900">
            4. Critical Alerts
          </div>
        </div>

        <button className="text-[11px] font-extrabold text-blue-700 hover:text-blue-900 transition flex items-center gap-1">
          View All <FaChevronRight className="text-[9px]" />
        </button>
      </div>
    </div>

    <div className="p-4 pt-3">
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="h-[200px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[10px] font-extrabold text-slate-500">
                <th className="px-3 py-2 text-left">Patient</th>
                <th className="px-3 py-2 text-left">Issue</th>
                <th className="px-3 py-2 text-left">Raised By</th>
                <th className="px-3 py-2 text-left">Action Taken</th>
                <th className="px-3 py-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {rows?.length ? (
                rows.slice(0, 4).map((r, idx) => (
                  <tr
                    key={`${r._key}-${idx}`}
                    className="hover:bg-slate-50/70 transition"
                  >
                    <td className="px-3 py-2.5 font-extrabold text-slate-900 whitespace-nowrap">
                      {r.patient}
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 font-semibold min-w-[240px]">
                      {r.issue}
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 font-bold whitespace-nowrap">
                      {r.raisedBy}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 font-semibold whitespace-nowrap">
                      {r.actionTaken}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <StatusBadge status={r.status} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-10 text-center text-slate-500 font-semibold"
                  >
                    No critical alerts in this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </Card>
);

/* =========================================================
CARD: Service Provider Share
========================================================= */
const ServiceProviderShareCard = ({
  className,
  donutData,
  totalCollection,
  hospitalValue,
  providerValue,
  commissionPaid,
  netCollection,
}) => (
  <Card className={className}>
    <div className="p-4 border-b border-slate-200">
      <div className="text-sm font-extrabold text-slate-900">
        5. Service Provider Share
      </div>
    </div>

    <div className="p-4">
      <div className="grid grid-cols-2 gap-3 items-center">
        <div className="h-[170px] relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={donutData}
                dataKey="value"
                nameKey="name"
                innerRadius={52}
                outerRadius={72}
                paddingAngle={2}
              >
                {donutData.map((x, i) => (
                  <Cell key={i} fill={x.color} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => inr(v)} />
            </PieChart>
          </ResponsiveContainer>

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
            <div className="text-[10px] font-extrabold text-slate-500">
              Total Collection
            </div>
            <div className="text-sm font-black text-slate-900 mt-1">
              {inr(totalCollection)}
            </div>
          </div>
        </div>

        <div className="space-y-3 text-[11px]">
          <LegendItem
            label="Hospital Share"
            value={inr(hospitalValue)}
            dot={DONUT_BLUE}
            pct={donutData?.[0]?.pct}
          />
          <LegendItem
            label="Provider Share"
            value={inr(providerValue)}
            dot={DONUT_GREEN}
            pct={donutData?.[1]?.pct}
          />
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <MiniBox
          label="Commission Paid"
          value={`${inr(commissionPaid)} (${pctOf(commissionPaid, totalCollection)}%)`}
        />
        <MiniBox label="Net Collection" value={inr(netCollection)} />
      </div>
    </div>
  </Card>
);

const LegendItem = ({ label, value, dot, pct }) => (
  <div className="flex items-start justify-between gap-2">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ background: dot }}
        />
        <span className="font-extrabold text-slate-700 truncate">{label}</span>
      </div>
      <div className="mt-1 text-slate-500 font-semibold">{pct ?? 0}%</div>
    </div>
    <div className="font-black text-slate-900 whitespace-nowrap">{value}</div>
  </div>
);

const MiniBox = ({ label, value }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
    <div className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">
      {label}
    </div>
    <div className="mt-1 text-xs font-black text-slate-900">{value}</div>
  </div>
);

/* =========================================================
CARD: Top Revenue Generators
========================================================= */
const TopRevenueGeneratorsCard = ({
  className,
  tab,
  setTab,
  rows,
  maxValue,
}) => (
  <Card className={className}>
    <div className="p-4 border-b border-slate-200">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-extrabold text-slate-900">
            9. Top Revenue Generators
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Chip active={tab === "service"} onClick={() => setTab("service")}>
              By Service
            </Chip>
            <Chip active={tab === "doctor"} onClick={() => setTab("doctor")}>
              By Doctor
            </Chip>
            <Chip
              active={tab === "referral"}
              onClick={() => setTab("referral")}
            >
              By Referral
            </Chip>
            <Chip active={tab === "branch"} onClick={() => setTab("branch")}>
              By Branch
            </Chip>
          </div>
        </div>

        <button className="text-[11px] font-extrabold text-blue-700 hover:text-blue-900 transition flex items-center gap-1 whitespace-nowrap">
          View All <FaChevronRight className="text-[9px]" />
        </button>
      </div>
    </div>

    <div className="p-4 pt-3">
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-[10px] font-extrabold text-slate-500">
              <th className="px-3 py-2 text-left w-12">Rank</th>
              <th className="px-3 py-2 text-left">Service Category</th>
              <th className="px-3 py-2 text-right w-[130px]">Collection (₹)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {tab === "service" ? (
              (rows || []).length ? (
                rows.map((r, idx) => (
                  <tr
                    key={`${r._key}-${idx}`}
                    className="hover:bg-slate-50/70 transition"
                  >
                    <td className="px-3 py-2.5 font-black text-slate-900">
                      {idx + 1}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-extrabold text-slate-900 truncate">
                          {r.category}
                        </span>
                        <div className="flex-1 max-w-[140px] h-2 bg-slate-100 rounded-md overflow-hidden border border-slate-200/60">
                          <div
                            className="h-2 bg-blue-600 rounded-md"
                            style={{
                              width: `${Math.round((r.value / Math.max(1, maxValue)) * 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-black text-slate-900 whitespace-nowrap">
                      {inr(r.value)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={3}
                    className="px-3 py-10 text-center text-slate-500 font-semibold"
                  >
                    No revenue data for this range.
                  </td>
                </tr>
              )
            ) : (
              <tr>
                <td
                  colSpan={3}
                  className="px-3 py-10 text-center text-slate-500 font-semibold"
                >
                  No active data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </Card>
);

const Chip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={cx(
      "px-3 py-1.5 rounded-md text-[11px] font-extrabold border transition",
      active
        ? "bg-blue-600 text-white border-blue-600"
        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50",
    )}
  >
    {children}
  </button>
);

/* =========================================================
CARD: Top Performers
========================================================= */
const TopPerformersCard = ({ className, tab, setTab, rows }) => (
  <Card className={className}>
    <div className="p-4 border-b border-slate-200">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-extrabold text-slate-900">
            8. Top Performers (Staff)
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Chip active={tab === "nursing"} onClick={() => setTab("nursing")}>
              Nursing
            </Chip>
            <Chip active={tab === "physio"} onClick={() => setTab("physio")}>
              Physio
            </Chip>
            <Chip active={tab === "doctors"} onClick={() => setTab("doctors")}>
              Doctors
            </Chip>
            <Chip active={tab === "lab"} onClick={() => setTab("lab")}>
              Lab
            </Chip>
          </div>
        </div>

        <button className="text-[11px] font-extrabold text-blue-700 hover:text-blue-900 transition flex items-center gap-1 whitespace-nowrap">
          View All <FaChevronRight className="text-[9px]" />
        </button>
      </div>
    </div>

    <div className="p-4 pt-3">
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-[10px] font-extrabold text-slate-500">
              <th className="px-3 py-2 text-left w-12">Rank</th>
              <th className="px-3 py-2 text-left">Staff Name</th>
              <th className="px-3 py-2 text-right">Completed</th>
              <th className="px-3 py-2 text-right">Rating</th>
              <th className="px-3 py-2 text-right">Collection (₹)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {(rows || []).length ? (
              rows.map((s, idx) => (
                <tr
                  key={`${s._key}-${idx}`}
                  className="hover:bg-slate-50/70 transition"
                >
                  <td className="px-3 py-2.5 font-black text-slate-900">
                    {idx + 1}
                  </td>
                  <td className="px-3 py-2.5 font-extrabold text-slate-900 truncate">
                    {s.name}
                  </td>
                  <td className="px-3 py-2.5 text-right font-black text-slate-900">
                    {s.completed}
                  </td>
                  <td className="px-3 py-2.5 text-right font-black text-slate-900">
                    <span className="inline-flex items-center gap-1 justify-end">
                      <FaStar className="text-amber-500 text-[11px]" />{" "}
                      {Number(s.rating || 0).toFixed(1)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right font-black text-slate-900 whitespace-nowrap">
                    {inr(s.collection)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-10 text-center text-slate-500 font-semibold"
                >
                  No staff data for this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </Card>
);

/* =========================================================
CARD: Incomplete Services
========================================================= */
const IncompleteServicesCard = ({ className, rows }) => (
  <Card className={className}>
    <div className="p-4 border-b border-slate-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-amber-50 border border-amber-100 text-amber-800 flex items-center justify-center">
            <FaClock className="text-[13px]" />
          </div>
          <div className="text-sm font-extrabold text-slate-900">
            5. Incomplete Services
          </div>
        </div>

        <button className="text-[11px] font-extrabold text-rose-600 hover:text-rose-800 transition flex items-center gap-1">
          View All <FaChevronRight className="text-[9px]" />
        </button>
      </div>
    </div>

    <div className="p-4 pt-3 h-[calc(100%-56px)]">
      <div className="border border-slate-200 rounded-md overflow-hidden h-full">
        <div className="overflow-auto h-full">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr className="text-[10px] font-extrabold text-slate-500">
                <th className="px-3 py-2 text-left">Patient</th>
                <th className="px-3 py-2 text-left">Service Missed</th>
                <th className="px-3 py-2 text-left">Reason</th>
                <th className="px-3 py-2 text-left">Corrective Action</th>
                <th className="px-3 py-2 text-right">Rescheduled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {rows?.length ? (
                rows.map((r, idx) => (
                  <tr
                    key={`${r._key}-${idx}`}
                    className="hover:bg-slate-50/70 transition"
                  >
                    <td className="px-3 py-2.5 font-extrabold text-slate-900 whitespace-nowrap">
                      {r.patient}
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 font-semibold whitespace-nowrap">
                      {r.serviceMissed}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 font-semibold">
                      {r.reason}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 font-semibold">
                      {r.correctiveAction}
                    </td>
                    <td className="px-3 py-2.5 text-right text-slate-700 font-bold whitespace-nowrap">
                      {r.rescheduled}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-10 text-center text-slate-500 font-semibold"
                  >
                    No incomplete services.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </Card>
);

/* =========================================================
CARD: Patient Feedback
========================================================= */
const PatientFeedbackTodayCard = ({
  className,
  avg,
  totalFeedback,
  negativeFeedback,
  rows,
  onViewAll,
}) => (
  <Card className={className}>
    <div className="p-4 border-b border-slate-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-blue-50 border border-blue-100 text-blue-700 flex items-center justify-center">
            <FaStar className="text-[13px]" />
          </div>
          <div className="text-sm font-extrabold text-slate-900">
            6. Patient Feedback
          </div>
        </div>

        <button
          onClick={onViewAll}
          className="text-[11px] font-extrabold text-rose-600 hover:text-rose-800 transition flex items-center gap-1"
        >
          View All <FaChevronRight className="text-[9px]" />
        </button>
      </div>
    </div>

    <div className="p-4 flex flex-col gap-3 h-[calc(100%-56px)] overflow-hidden">
      <div className="grid grid-cols-3 gap-2 shrink-0">
        <MiniStatTop
          label="Average Rating"
          value={
            <div className="flex items-center gap-1.5 justify-center">
              <FaStar className="text-amber-500 text-sm" />
              <span className="font-black text-slate-900">
                {Number(avg || 0).toFixed(1)}
              </span>
              <span className="text-slate-500 font-extrabold text-[11px]">
                / 5
              </span>
            </div>
          }
        />
        <MiniStatTop
          label="Total Feedback"
          value={
            <span className="font-black text-slate-900">{totalFeedback}</span>
          }
        />
        <MiniStatTop
          label="Negative Feedback"
          value={
            <span className="font-black text-slate-900">
              {negativeFeedback}
            </span>
          }
        />
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider shrink-0">
          Recent Feedback
        </div>

        <div className="divide-y divide-slate-100 bg-white overflow-y-auto flex-1 min-h-0 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
          {(rows || []).length ? (
            rows.map((r) => (
              <div
                key={r._key}
                className="px-3 py-2.5 flex items-start justify-between gap-3"
              >
                <div className="min-w-0">
                  <Stars value={r.rating} />
                  <div className="mt-1 text-[11px] font-semibold text-slate-700 truncate">
                    “{r.message}”
                  </div>
                  <div className="mt-1 text-[10px] font-bold text-slate-500">
                    Patient: {r.name} | Staff: {r.hcpName}
                  </div>
                </div>
                <div className="text-[10px] font-extrabold text-slate-400 whitespace-nowrap">
                  {r.time}
                </div>
              </div>
            ))
          ) : (
            <div className="px-3 py-10 text-center text-slate-500 font-semibold text-xs">
              No feedback in this range.
            </div>
          )}
        </div>

        <div className="px-3 py-2.5 bg-white border-t border-slate-100 shrink-0">
          <button
            onClick={onViewAll}
            className="w-full text-center text-[11px] font-extrabold text-blue-700 hover:text-blue-900 transition"
          >
            View all feedback{" "}
            <FaChevronRight className="inline-block ml-1 text-[9px]" />
          </button>
        </div>
      </div>
    </div>
  </Card>
);

const MiniStatTop = ({ label, value }) => (
  <div className="rounded-md border border-slate-200 bg-white p-2.5 text-center">
    <div className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">
      {label}
    </div>
    <div className="mt-1 text-sm">{value}</div>
  </div>
);

export default PartnerDashboard;
